// Full-screen overlay that bridges a UI-mode switch (new ⇄ classic). The switch
// does window.location.reload(), so the overlay must survive the reload: we set
// a sessionStorage flag before reloading and, on the very next boot, re-show the
// same overlay BEFORE React paints — otherwise the reloading page and the fresh
// app's first (intermediate) paint flash through.

// import { setNewUIEnabled } from 'hooks/useNewUI';
import {
  clearUiChoice, designOutOfDate, refreshConfig, setNewUIEnabled,
} from 'hooks/useNewUI';

const FLAG = 'uiSwitchInProgress';
const STYLE_ID = 'ui-switch-overlay-style';
let hideTimer = null;

const ensureStyles = () => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
  @keyframes uiSwitchSpin { to { transform: rotate(360deg); } }
  .uiSwitchOverlay {
    position: fixed; inset: 0; z-index: 2147483000;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 20px; background: #ffffff; opacity: 1;
    font-family: 'Montserrat', sans-serif;
  }
  .uiSwitchOverlay.hiding { opacity: 0; transition: opacity .35s ease; }
  .uiSwitchSpinner {
    width: 42px; height: 42px; border-radius: 50%;
    border: 3px solid rgba(21,23,28,.12); border-top-color: #5aa5ef;
    animation: uiSwitchSpin .9s linear infinite;
  }
  .uiSwitchText { color: #6b7280; font-size: 14px; font-weight: 600; letter-spacing: .2px; }
  @media (prefers-color-scheme: dark) {
    .uiSwitchOverlay { background: #0e0f13; }
    .uiSwitchText { color: rgba(255,255,255,.6); }
  }
  `;
  document.head.appendChild(s);
};

const showOverlay = () => {
  try {
    ensureStyles();
    if (document.getElementById('ui-switch-overlay')) return;
    const el = document.createElement('div');
    el.id = 'ui-switch-overlay';
    el.className = 'uiSwitchOverlay';
    el.innerHTML = '<div class="uiSwitchSpinner"></div><div class="uiSwitchText">Switching layout…</div>';
    document.body.appendChild(el);
  } catch (e) { /* noop */ }
};

const hideOverlay = () => {
  try {
    const el = document.getElementById('ui-switch-overlay');
    if (!el) return;
    el.classList.add('hiding');
    setTimeout(() => { try { el.remove(); } catch (e) { /* noop */ } }, 380);
  } catch (e) { /* noop */ }
};

// Called from a toggle: flip the flag, cover the screen, then reload. The
// overlay stays up (old DOM is frozen until the browser swaps in the new page).
// Does nothing when the backend config doesn't allow this user to switch.
export const beginUiSwitch = (toNewUI) => {
  if (!setNewUIEnabled(!!toNewUI)) return;
  try {
    // setNewUIEnabled(!!toNewUI); // old: always switched, no canSwitch check
    sessionStorage.setItem(FLAG, '1');
  } catch (e) { /* noop */ }
  showOverlay();
  // Let the overlay paint before the navigation begins.
  setTimeout(() => { window.location.reload(); }, 30);
};

// How long logout waits for the signed-out getConfig before falling back to the cache.
const LOGOUT_CONFIG_WAIT_MS = 1500;

const reloadBehindOverlay = () => {
  try {
    sessionStorage.setItem(FLAG, '1');
  } catch (e) { /* noop */ }
  showOverlay();
  setTimeout(() => { window.location.reload(); }, 30);
};

// Called right after logout. The user's pick is deleted, so the sign-in screen
// must show the backend's default_ui (from the signed-out getConfig), not the
// design the last user picked. The design is fixed per page load, so if it has
// to change, reload behind the switch overlay.
export const settleDesignAfterLogout = async () => {
  clearUiChoice();
  // Cover right away if the cached config already says the design will change.
  const coveredEarly = designOutOfDate();
  if (coveredEarly) showOverlay();
  await Promise.race([
    refreshConfig({ token: null }),
    new Promise((resolve) => { setTimeout(resolve, LOGOUT_CONFIG_WAIT_MS); }),
  ]);
  if (designOutOfDate()) reloadBehindOverlay();
  else if (coveredEarly) hideOverlay();
};

// Called once at app boot (index.js), synchronously, before React renders — if a
// switch is in progress, re-show the overlay immediately so the fresh app mounts
// behind it, then fade it out after the first paint settles.
export const resumeUiSwitchOverlay = () => {
  try {
    if (sessionStorage.getItem(FLAG) !== '1') return;
    sessionStorage.removeItem(FLAG);
  } catch (e) { return; }
  showOverlay();
  if (hideTimer) clearTimeout(hideTimer);
  // Hold past the app's initial mount + route resolution, then reveal.
  hideTimer = setTimeout(hideOverlay, 900);
};

export default beginUiSwitch;
